import React, { Key } from 'react';
import { ColumnType } from 'antd/es/table';
import { EUpdateStatus } from 'common/models/commonModel';
import { priceFormatter } from 'common/helpers/format.helper';
import { dateFormatter } from 'common/helpers/date.helper';
import { PurchaseOrderStatus } from 'entities/PurchaseOrder/components/PurchaseOrderStatus';

export interface IPurchaseOrderCollectionParams extends Paths.PurchaseOrderControllerList.QueryParameters {}
export interface IPurchaseOrderCollection extends Components.Schemas.PurchaseOrderCollectionDto {}
export interface IPurchaseOrder extends Components.Schemas.PurchaseOrderDto {}

export interface IPurchaseOrderModelParams extends Paths.PurchaseOrderControllerGetBySonum.PathParameters {}
export interface IPurchaseOrderModel extends Components.Schemas.PurchaseOrderDetailedDto {}
export interface IPurchaseOrderCreatePayload extends Components.Schemas.CreatePurchaseOrderDto {}
export interface IPurchaseOrderItem extends Components.Schemas.PurchaseOrderItemDto {
  key: Key;
}

export interface IPurchaseOrderStoreCollection extends Components.Schemas.PurchaseOrderStoreCollectionDto {}
export interface IPurchaseOrderStoreModel extends Components.Schemas.PurchaseOrderStoreDto {
  id?: string | number;
}

// MBC-85: Track PO RECO column items.
export interface IPurchaseOrderRecoStoreModel extends Components.Schemas.PurchaseOrderRecoStoreDto {}
export interface IPurchaseOrderRecoStoreCollection extends Components.Schemas.PurchaseOrderRecoStoreCollectionDto {}

export interface IPurchaseOrderRecoParams extends Paths.PurchaseOrderControllerGetReco.QueryParameters {}
export interface IPurchaseOrderRecoModel extends Components.Schemas.PurchaseOrderRecoDto {}
export interface IPurchaseOrderRecoProduct extends Components.Schemas.PurchaseOrderProductDto {
  key: Key;
  orderCount: number;
  status?: EUpdateStatus;
}

export interface IPurchaseOrderOutstandingPayablesModel
  extends Components.Schemas.PurchaseOrderOutstandingPayablesDto {}

export interface IPurchaseOrderRecoBasisFilters extends Paths.PurchaseOrderControllerGetRecoBasis.QueryParameters {}
export interface IPurchaseOrderRecoBasisModel extends Components.Schemas.PurchaseOrderRecoBasisDto {}

export interface IRecoBasisUnitsSoldDataSourceItem {
  key: string;
  criteria: string;
  sun: number;
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
}

export interface IRecoBasisTrendDataSourceItem {
  key: string;
  last4Wk: number;
  last8Wk: number;
  trendFactor: number;
}

export interface IRecoBasisSellingPeriodDataSourceItem {
  key: string;
  sellingPeriod: string;
  periodSum: number;
  currentInventory: number;
  initialReco: number;
}

export interface IRecoBasisProductSetDataSourceItem {
  key: string;
  productSet: number;
  adjInitialReco: number;
}

export interface IRecoBasisSafetyStockDataSourceItem {
  key: string;
  sellingPeriod: string;
  safetyStock: number;
  adjInitialReco: number;
  reco: number;
}

export interface IRecoBasisDataSources {
  unitsSoldDataSource: IRecoBasisUnitsSoldDataSourceItem[];
  trendDataSource: IRecoBasisTrendDataSourceItem[];
  adjDemandDataSource: IRecoBasisUnitsSoldDataSourceItem[];
  sellingPeriodDataSource: IRecoBasisSellingPeriodDataSourceItem[];
  productSetDataSource: IRecoBasisProductSetDataSourceItem[];
  safetyStockDataSource: IRecoBasisSafetyStockDataSourceItem[];
}

export enum EPurchaseOrderStatus {
  ForApproval = 'forApproval',
  Preparing = 'preparing',
  InTransit = 'inTransit',
  Delivered = 'delivered',
  Canceled = 'canceled',
}

export enum EPurchaseOrderStatusLabel {
  forApproval = 'For Approval',
  preparing = 'Preparing Order',
  inTransit = 'In Transit',
  delivered = 'Delivered',
  canceled = 'Canceled',
}

export enum EPurchaseOrderStatusFilterKey {
  // MBC-85: Track PO RECO column — stores without active SO on next delivery date.
  Reco = 'reco',
  ForApproval = 'forApproval',
  Preparing = 'preparing',
  InTransit = 'inTransit',
  DeliveredCanceled = 'delivered/canceled',
}

export enum EPurchaseOrderStatusFilterLabel {
  reco = 'RECO',
  forApproval = 'For Approval',
  preparing = 'Preparing Order',
  inTransit = 'In Transit',
  'delivered/canceled' = 'Delivered/Canceled',
}

const alignEnd = {
  onHeaderCell: () => ({ style: { textAlign: 'end' as const } }),
  onCell: () => ({ style: { textAlign: 'end' as const } }),
};

export const getPurchaseOrderItemsConfig = (status?: string): ColumnType<IPurchaseOrderItem>[] => [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Item',
  },
  {
    key: 'orderCount',
    dataIndex: 'orderCount',
    title: 'SO Qty',
    ...alignEnd,
  },
  // DR card (delivered): actually received qty + variance vs ordered.
  ...(status === EPurchaseOrderStatus.Delivered
    ? ([
        {
          key: 'siQty',
          dataIndex: 'siQty',
          title: 'SI Qty',
          ...alignEnd,
        },
        {
          key: 'variance',
          dataIndex: 'variance',
          title: 'Var',
          ...alignEnd,
        },
      ] as ColumnType<IPurchaseOrderItem>[])
    : []),
  {
    key: 'price',
    dataIndex: 'price',
    title: 'Unit Cost',
    ...alignEnd,
    render: (_, record) => priceFormatter(record.price),
  },
  {
    key: 'total',
    title: 'Adjusted Total Cost',
    ...alignEnd,
    // BE-computed adjustedTotalCost: SO statuses — SO_QTY × price, delivered (DR) — SI_QTY × price.
    render: (_, record) => priceFormatter(record.adjustedTotalCost),
  },
];

export const deliveryHistoryConfig: ColumnType<IPurchaseOrder>[] = [
  {
    key: 'storeCode',
    dataIndex: 'storeCode',
    title: 'Store Code',
    sorter: true,
  },
  {
    key: 'sonum',
    dataIndex: 'sonum',
    title: 'SO#',
    sorter: true,
  },
  {
    key: 'orderedAt',
    dataIndex: 'orderedAt',
    title: 'Ordered',
    sorter: true,
    render: (text) => dateFormatter(text, 'MM/DD/YYYY HH:mm'),
  },
  {
    key: 'amount',
    dataIndex: 'amount',
    title: 'Order Amount',
    sorter: true,
    render: (text) => priceFormatter(text),
    onHeaderCell: () => {
      return {
        style: { display: 'flex', justifyContent: 'end' },
        className: '',
      };
    },
    onCell: () => {
      return {
        style: { textAlign: 'end' },
      };
    },
  },
  {
    // MBC-79: receivedBy is not a direct sosum column (resolved via join);
    // BE EPurchaseOrderSort doesn't cover it — keep column visible, but non-sortable.
    key: 'receivedBy',
    dataIndex: 'receivedBy',
    title: 'Received',
  },
  {
    key: 'deliveredAt',
    dataIndex: 'deliveredAt',
    title: 'Delivered',
    sorter: true,
    render: (text) => dateFormatter(text, 'MM/DD/YYYY HH:mm'),
  },
  {
    key: 'status',
    dataIndex: 'status',
    title: 'Order Status',
    sorter: true,
    render: (text) => <PurchaseOrderStatus status={text} />,
  },
];

export const recoBasisUnitsSoldDataConfig = [
  {
    key: 'criteria',
    title: 'Criteria',
    dataIndex: 'criteria',
    width: 122,
  },
  {
    key: 'sun',
    title: 'S',
    dataIndex: 'sun',
  },
  {
    key: 'mon',
    title: 'M',
    dataIndex: 'mon',
  },
  {
    key: 'tue',
    title: 'T',
    dataIndex: 'tue',
  },
  {
    key: 'wed',
    title: 'W',
    dataIndex: 'wed',
  },
  {
    key: 'thu',
    title: 'T',
    dataIndex: 'thu',
  },
  {
    key: 'fri',
    title: 'F',
    dataIndex: 'fri',
  },
  {
    key: 'sat',
    title: 'S',
    dataIndex: 'sat',
  },
];

export const recoBasisTrendConfig = [
  {
    key: 'last4Wk',
    title: 'Last 4 wk',
    dataIndex: 'last4Wk',
    width: 122,
  },
  {
    key: 'last8Wk',
    title: 'Last 8 wk',
    dataIndex: 'last8Wk',
    width: 122,
  },
  {
    key: 'trendFactor',
    title: 'Trend Factor',
    dataIndex: 'trendFactor',
  },
];

export const recoBasisSellingPeriodConfig = [
  {
    key: 'sellingPeriod',
    title: 'Selling Period',
    dataIndex: 'sellingPeriod',
    width: 122,
  },
  {
    key: 'periodSum',
    title: 'Period Sum',
    dataIndex: 'periodSum',
    width: 122,
  },
  {
    key: 'currentInventory',
    title: 'Current Inv.',
    dataIndex: 'currentInventory',
    width: 122,
  },
  {
    key: 'initialReco',
    title: 'Initial Reco',
    dataIndex: 'initialReco',
  },
];

export const recoBasisProductSetConfig = [
  {
    key: 'productSet',
    title: 'Product Set',
    dataIndex: 'productSet',
    width: 122,
  },
  {
    key: 'adjInitialReco',
    title: 'Adj. Initial Reco',
    dataIndex: 'adjInitialReco',
  },
];

export const recoBasisSafetyStockConfig = [
  {
    key: 'sellingPeriod',
    title: '1/3 of',
    dataIndex: 'sellingPeriod',
    width: 122,
  },
  {
    key: 'safetyStock',
    title: 'Safety Stock',
    dataIndex: 'safetyStock',
    width: 122,
  },
  {
    key: 'adjInitialReco',
    title: 'Adj. Initial Reco',
    dataIndex: 'adjInitialReco',
    width: 122,
  },
  {
    key: 'reco',
    title: 'Reco',
    dataIndex: 'reco',
  },
];

export const VIEW_DELIVERY_HISTORY_SORT_FIELD: Record<string, string> = {
  storeCode: 'storeCode',
  sonum: 'sonum',
  orderedAt: 'createdAt',
  amount: 'amount',
  deliveredAt: 'updatedAt',
  status: 'status',
};

export const VIEW_DELIVERY_HISTORY_LIMIT = 50;
